import { useCallback, useRef } from "react";
import type { UUID } from "../../../__definitions__/iconfinder.changeengine.com-definitions";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { DisplayConditionData } from "../../unlayer-email-content/components/display-condition-dialog";
import type { DisplayConditionReturn } from "../../unlayer-email-content/hooks/use-display-condition";

/**
 * Follow-up variant of useDisplayCondition that doesn't depend on useFormContext.
 * Follow-ups don't support dynamic content, so the segment map management is omitted.
 */
export const useFollowUpDisplayCondition = (): DisplayConditionReturn => {
  const { open, handleClose, handleOpen, data: selectedId } = useDialog<UUID | undefined>();

  const doneCallbackRef = useRef<((data: DisplayConditionData) => void) | null>(null);

  const onDisplayConditionRequest = useCallback(
    (dcData: DisplayConditionData, done: (data: DisplayConditionData) => void) => {
      doneCallbackRef.current = done;
      handleOpen(dcData.id);
    },
    [handleOpen],
  );

  const onConfirm = useCallback(
    (conditionData: DisplayConditionData) => {
      if (doneCallbackRef.current) {
        doneCallbackRef.current(conditionData);
        doneCallbackRef.current = null;
      }
      handleClose();
    },
    [handleClose],
  );

  const onClose = useCallback(() => {
    handleClose();
    doneCallbackRef.current = null;
  }, [handleClose]);

  return { isOpen: open, onDisplayConditionRequest, onConfirm, onClose, selectedId };
};
